import React, { useEffect, useState } from "react";
import ModuleAdmin from "./ModuleAdmin";
import { Pencil, Trash2, Plus, X, Check } from "lucide-react";

const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

const CourseAdmin = () => {
  const [courses, setCourses] = useState([]);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const fetchCourses = async () => {
    try {
      const res = await fetch(`${apiBaseURL}/courses`);
      if (!res.ok) throw new Error("Failed to fetch courses");
      const data = await res.json();
      setCourses(data.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const deleteCourse = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    await fetch(`${apiBaseURL}/courses/${id}`, { method: "DELETE" });
    fetchCourses();
  };

  return (
    <div className="container mx-auto font-gotham">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Course Admin</h1>
        <button
          className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md text-sm"
          onClick={() => {
            setEditingCourse(null);
            setFormOpen(true);
          }}
        >
          <Plus className="w-4 h-4" />
          Add Course
        </button>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg shadow-lg border border-gray-200">
        <table className="table-auto w-full text-sm text-gray-700">
          <thead className="bg-teal-50 text-gray-800">
            <tr>
              <th className="px-6 py-3 text-left font-semibold">Slug</th>
              <th className="px-6 py-3 text-left font-semibold">Title</th>
              <th className="px-6 py-3 text-left font-semibold">Order</th>
              <th className="px-6 py-3 text-left font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {courses.map((course) => (
              <React.Fragment key={course.id}>
                <tr className="hover:bg-gray-100 border-t">
                  <td className="px-6 py-4">{course.slug}</td>
                  <td className="px-6 py-4">{course.title}</td>
                  <td className="px-6 py-4">{course.order || "-"}</td>
                  <td className="px-6 py-4 flex gap-3">
                    <button
                      className="text-yellow-600 hover:text-yellow-700"
                      onClick={() => {
                        setEditingCourse(course);
                        setFormOpen(true);
                      }}
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      className="text-red-600 hover:text-red-700"
                      onClick={() => deleteCourse(course.id)}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
                {/* Inline Module Manager */}
                <tr>
                  <td colSpan={4} className="bg-gray-50 p-3">
                    <ModuleAdmin course={course} />
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {formOpen && (
        <CourseForm
          course={editingCourse}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            fetchCourses();
          }}
        />
      )}
    </div>
  );
};

const CourseForm = ({ course, onClose, onSaved }) => {
  const [form, setForm] = useState(
    course || { slug: "", title: "", description: "", order: 1 }
  );

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleSubmit = async () => {
    const url = course?.id
      ? `${apiBaseURL}/courses/${course.id}`
      : `${apiBaseURL}/courses`;
    const method = course?.id ? "PUT" : "POST";

    await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg shadow-lg w-[500px] space-y-4 border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">
          {course ? "Edit Course" : "Create Course"}
        </h2>

        <input
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Slug"
          value={form.slug}
          onChange={(e) => handleChange("slug", e.target.value)}
        />
        <input
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Title"
          value={form.title}
          onChange={(e) => handleChange("title", e.target.value)}
        />
        <textarea
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Description"
          value={form.description}
          onChange={(e) => handleChange("description", e.target.value)}
        />
        <input
          type="number"
          className="border border-gray-300 rounded-md p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Order"
          value={form.order}
          onChange={(e) => handleChange("order", parseInt(e.target.value))}
        />

        <div className="flex justify-end space-x-2">
          <button
            className="flex items-center gap-1 bg-gray-400 hover:bg-gray-500 text-white px-3 py-2 rounded-md text-sm"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
          <button
            className="flex items-center gap-1 bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-md text-sm"
            onClick={handleSubmit}
          >
            <Check className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseAdmin;
